/**
 * ProductForm — the canonical "Add / Edit product" form used across the app.
 *
 * Single source of truth so the Inventory page, the Purchase-Order create
 * flow, and the Direct-Receipt flow all present the same fields, labels,
 * data-testids and validation rules.
 *
 * Controlled: parent owns the `value` and the `onChange` callback fires on
 * every edit. The `onSubmit` handler receives the final payload; it is only
 * invoked after the "Product Name" + "Unit Price" required checks pass.
 *
 * Layout: matches InventoryPage — 2-column grid inside a scroll area, with
 * a footer of Cancel / Save buttons.
 */
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRef, useState } from 'react';
import { MultiImageUploadField } from './MultiImageUploadField';

export const PRODUCT_CATEGORIES = [
  'General', 'Pain Relief', 'Antibiotics', 'Allergy', 'Gastric', 'Diabetes',
  'Cardiovascular', 'Vitamins', 'Cough & Cold', 'Respiratory', 'Dermatology', 'Other',
];

export const emptyProduct = {
  name: '', generic_name: '', category: 'General', manufacturer: '',
  unit_price: 0, unit_cost: 0, stock_quantity: 0, min_stock_level: 10,
  requires_prescription: false, is_controlled: false, is_otc: false, is_active: true, allow_open_sale: false, image_url: '',
  photos: [], description: '', barcode: '', batches: [],
};

export default function ProductForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  saving = false,
  submitLabel = 'Add Product',
  showStock = true,
  showCost = true,
  testidPrefix = 'product',
  productId,
}) {
  const [photoUploading, setPhotoUploading] = useState(false);
  const currentValue = useRef(value);
  currentValue.current = value;
  const set = (patch) => onChange({ ...value, ...patch });

  const handleSubmit = () => {
    if (!value.name?.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (Number(value.unit_price || 0) < 0) {
      toast.error('Unit price cannot be negative');
      return;
    }
    onSubmit(value);
  };

  return (
    <>
      <ScrollArea className="flex-1 max-h-[65vh] pr-4">
        <div className="space-y-4 pb-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2">
              <Label>Product Name *</Label>
              <Input
                value={value.name || ''}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Paracetamol 500mg"
                data-testid={`${testidPrefix}-name-input`}
              />
            </div>
            <div className="space-y-2">
              <Label>Generic Name</Label>
              <Input
                value={value.generic_name || ''}
                onChange={(e) => set({ generic_name: e.target.value })}
                placeholder="e.g. Acetaminophen"
                data-testid={`${testidPrefix}-generic-input`}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={value.category || 'General'} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger data-testid={`${testidPrefix}-category-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Manufacturer</Label>
              <Input
                value={value.manufacturer || ''}
                onChange={(e) => set({ manufacturer: e.target.value })}
                placeholder="e.g. Cipla"
                data-testid={`${testidPrefix}-manufacturer-input`}
              />
            </div>
            <div className="space-y-2">
              <Label>Selling Price *</Label>
              <Input
                type="number" step={0.01} min={0}
                value={value.unit_price ?? 0}
                onChange={(e) => set({ unit_price: parseFloat(e.target.value) || 0 })}
                data-testid={`${testidPrefix}-price-input`}
              />
            </div>
            {showCost && (
              <div className="space-y-2">
                <Label>Unit Cost</Label>
                <Input
                  type="number" step={0.01} min={0}
                  value={value.unit_cost ?? 0}
                  onChange={(e) => set({ unit_cost: parseFloat(e.target.value) || 0 })}
                  data-testid={`${testidPrefix}-cost-input`}
                />
              </div>
            )}
            {showStock && (
              <div className="space-y-2">
                <Label>Stock Quantity</Label>
                <Input
                  type="number" min={0}
                  value={value.stock_quantity ?? 0}
                  onChange={(e) => set({ stock_quantity: parseInt(e.target.value, 10) || 0 })}
                  data-testid={`${testidPrefix}-stock-input`}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Min Stock Level</Label>
              <Input
                type="number" min={0}
                value={value.min_stock_level ?? 10}
                onChange={(e) => set({ min_stock_level: parseInt(e.target.value, 10) || 0 })}
                data-testid={`${testidPrefix}-min-stock-input`}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={!!value.requires_prescription}
              onCheckedChange={(v) => set({ requires_prescription: v, ...(v ? { is_otc: false } : {}) })}
              data-testid={`${testidPrefix}-rx-switch`}
            />
            <Label>Requires Prescription</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={!!value.is_controlled}
              onCheckedChange={(v) => set({ is_controlled: v, ...(v ? { requires_prescription: true, is_otc: false } : {}) })}
              data-testid={`${testidPrefix}-controlled-switch`}
            />
            <Label className="flex items-center gap-1.5">
              Controlled Drug (Narcotic)
              {value.is_controlled && <Badge variant="destructive" className="text-[9px] px-1 py-0">Controlled</Badge>}
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={!!value.is_otc} disabled={!!value.requires_prescription || !!value.is_controlled}
              onCheckedChange={v => set({ is_otc: v })} data-testid={`${testidPrefix}-otc-switch`} />
            <Label>OTC — offer in patient shop</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={value.is_active !== false} onCheckedChange={v => set({ is_active: v })} data-testid={`${testidPrefix}-active-switch`} />
            <Label>Active product</Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch checked={!!value.allow_open_sale} onCheckedChange={v => set({ allow_open_sale: v })}
              data-testid={`${testidPrefix}-allow-open-sale-switch`} className="mt-0.5" />
            <Label className="leading-snug">
              Allow open-box (loose unit) sale
              <span className="block text-xs text-muted-foreground font-normal">
                When off, this product can only be sold in whole packs — selling less than a full pack is blocked at the till.
              </span>
            </Label>
          </div>
          {value.is_otc && !value.requires_prescription && !value.is_controlled && <MultiImageUploadField
            key={productId || 'new'} label="Photos (up to 4)" value={value.photos || []}
            onChange={photos => onChange({ ...currentValue.current, photos })} endpoint="/upload/product-photo"
            fields={{ product_id: productId, otc_selected: true }} testId={`${testidPrefix}-photos`} disabled={saving}
            onBusyChange={setPhotoUploading} />}

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={value.description || ''}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Brief description"
              data-testid={`${testidPrefix}-description-input`}
            />
          </div>
          <div className="space-y-2">
            <Label>Barcode / SKU</Label>
            <Input
              value={value.barcode || ''}
              onChange={(e) => set({ barcode: e.target.value })}
              placeholder="e.g. 8901234567890"
              data-testid={`${testidPrefix}-barcode-input`}
            />
          </div>
        </div>
      </ScrollArea>
      <div className="flex justify-end gap-2 pt-3 border-t">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} data-testid={`${testidPrefix}-cancel-btn`}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={saving || photoUploading} data-testid={`${testidPrefix}-save-btn`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {submitLabel}
        </Button>
      </div>
    </>
  );
}
